import React, { useCallback, useRef, useState } from "react";

import { QuizSection } from "../shared/QuizSection";
import {
  AllergiesQuestion,
  HealthIssuesQuestion,
  CurrentFoodQuestion,
  TriedFreshQuestion,
  FussinessQuestion,
  GoalsQuestion,
  FussyReassure,
} from "../questions";
import { useRegisterSubSteps } from "@/state/SubStepContext";
import { CurrentFood, isFussy } from "@/helpers/enums";

type Dog = { id: string; form: any };

type Props = {
  dogs: Dog[];
  updateDog: (dogId: string, update: any) => void;
};

/**
 * End of the MY DOG stage (issues #52 + #54), immediately before lead capture:
 *
 *   allergies → health issues → current food → tried fresh? → fussiness
 *   → (fussy reassurance) → goals
 *
 * Two sub-steps are conditional:
 *   - "triedFresh" only exists while at least one dog is not already on fresh
 *     food (issue #52 A2 gates on A1).
 *   - "fussyNote" is the reassurance interstitial, fired only when a dog is on
 *     one of the two fussy tiers (issue #52 B).
 * The sub-step list recomputes from answers, and useRegisterSubSteps re-registers
 * whenever its contents change, so conditional screens appear/disappear live.
 */

/** A dog needs the "tried fresh" question unless it already eats fresh food. */
function needsTriedFresh(form: any): boolean {
  const foods: string[] = form?.currentFood ?? [];
  return !foods.includes(CurrentFood.FRESH);
}

export default function StepTreats({ dogs, updateDog }: Props) {
  const dogsRef = useRef(dogs);
  dogsRef.current = dogs;

  const includeTriedFresh = dogs.some((d) => needsTriedFresh(d.form));
  const includeFussyNote = dogs.some((d) => isFussy(d.form?.fussiness));

  const SUB_STEPS = [
    "allergies",
    "health",
    "currentFood",
    ...(includeTriedFresh ? ["triedFresh"] : []),
    "fussiness",
    ...(includeFussyNote ? ["fussyNote"] : []),
    "goals",
  ];

  const getFurthestSubStep = (): string => {
    const all = dogs;
    if (!all.every((d) => (d.form?.allergies ?? []).length > 0)) return "allergies";
    if (!all.every((d) => (d.form?.healthIssues ?? []).length > 0)) return "health";
    if (!all.every((d) => (d.form?.currentFood ?? []).length > 0)) return "currentFood";
    if (includeTriedFresh && !all.every((d) => !needsTriedFresh(d.form) || !!d.form?.triedFresh)) return "triedFresh";
    if (!all.every((d) => !!d.form?.fussiness)) return "fussiness";
    if (!all.every((d) => !!d.form?.feedingGoal)) return "goals";
    return "goals";
  };

  const [subStep, setSubStepLocal] = useState<string>(getFurthestSubStep);

  const handleStepClick = useCallback((stepId: string) => {
    setSubStepLocal(stepId);
  }, []);

  const canAdvanceSubStep = useCallback((step: string): boolean => {
    const all = dogsRef.current;
    switch (step) {
      case "allergies":
        return all.every((d) => (d.form?.allergies ?? []).length > 0);
      case "health":
        return all.every((d) => (d.form?.healthIssues ?? []).length > 0);
      case "currentFood":
        return all.every((d) => (d.form?.currentFood ?? []).length > 0);
      case "triedFresh":
        // Only dogs that aren't already on fresh food need an answer.
        return all.every((d) => !needsTriedFresh(d.form) || !!d.form?.triedFresh);
      case "fussiness":
        return all.every((d) => !!d.form?.fussiness);
      case "fussyNote":
        return true; // reassurance screen, nothing to answer
      case "goals":
        return all.every((d) => !!d.form?.feedingGoal);
      default:
        return false;
    }
  }, []);

  useRegisterSubSteps(SUB_STEPS, subStep, handleStepClick, canAdvanceSubStep);

  return (
    <QuizSection>
      {subStep === "allergies" &&
        dogs.map((dog) => (
          <AllergiesQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.allergies ?? []}
            onChange={(newAllergies) => updateDog(dog.id, { allergies: newAllergies as any })}
          />
        ))}

      {subStep === "health" &&
        dogs.map((dog) => (
          <HealthIssuesQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.healthIssues ?? []}
            otherText={dog.form?.healthIssuesOther ?? ""}
            onChange={(issues, otherText) =>
              updateDog(dog.id, { healthIssues: issues, healthIssuesOther: otherText ?? "" })
            }
          />
        ))}

      {subStep === "currentFood" &&
        dogs.map((dog) => (
          <CurrentFoodQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.currentFood ?? []}
            brand={dog.form?.currentFoodBrand ?? ""}
            onChange={(foods, brand) =>
              updateDog(dog.id, { currentFood: foods, currentFoodBrand: brand ?? "" })
            }
          />
        ))}

      {subStep === "triedFresh" &&
        dogs
          .filter((dog) => needsTriedFresh(dog.form))
          .map((dog) => (
            <TriedFreshQuestion
              key={dog.id}
              dogName={dog.form?.name || "your dog"}
              value={dog.form?.triedFresh}
              onChange={(value) => updateDog(dog.id, { triedFresh: value })}
            />
          ))}

      {subStep === "fussiness" &&
        dogs.map((dog) => (
          <FussinessQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            value={dog.form?.fussiness}
            onChange={(value) => updateDog(dog.id, { fussiness: value })}
          />
        ))}

      {subStep === "fussyNote" && (
        <FussyReassure
          fussyDogNames={dogs
            .filter((d) => isFussy(d.form?.fussiness))
            .map((d) => d.form?.name || "your dog")}
        />
      )}

      {subStep === "goals" &&
        dogs.map((dog) => (
          <GoalsQuestion
            key={dog.id}
            dogName={dog.form?.name || "your dog"}
            sex={dog.form?.sex}
            value={dog.form?.feedingGoal}
            onChange={(value) => updateDog(dog.id, { feedingGoal: value })}
          />
        ))}
    </QuizSection>
  );
}
